'use client';

import { SIDEBAR_WIDTH_NAV_V3 } from '@heidi/design-system/page-shell';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { useDirection } from '@radix-ui/react-direction';
import { useCallback, useEffect, useState, type ReactElement, type Ref } from 'react';
import { useIntl } from 'react-intl';
import { navV3Messages } from '../nav-v3.messages';
import { HomePreviewContent } from './home-preview-content';
import { useHomeHoverPreview } from './use-home-hover-preview';

export interface HomeHoverPreviewProps {
  /**
   * Renders the Home trigger row, taking the ref this component needs on
   * the actual row DOM node — for the Popover anchor's position and for
   * the hover listeners below. A render-prop (rather than cloning a
   * passed-in element) keeps ref wiring explicit at the one call site that
   * needs it. The second argument is whether the preview is currently open,
   * so the trigger row can stay visually highlighted for as long as the
   * preview is open — not just while the pointer is literally over the row.
   */
  children: (ref: Ref<HTMLLIElement>, isOpen: boolean) => ReactElement;
  /** Same base-chat href the Home trigger itself navigates to. */
  baseChatHref: string;
}

/**
 * Anchors the Home hover Recents preview to the primary Home nav row.
 * A controlled, non-modal `Popover` (not `HoverCard`) because the content
 * holds focusable rows and actions. Home's own click/navigation behavior is
 * untouched — this only adds a preview that opens on hover and never
 * intercepts the row's click.
 */
export const HomeHoverPreview = ({ children, baseChatHref }: HomeHoverPreviewProps) => {
  const intl = useIntl();
  // `side` is a physical Radix value, not a logical one — resolve the
  // inline-end side from the ambient direction contract (`AppDirectionProvider`)
  // so the preview opens to the trailing edge in both LTR and RTL locales.
  const dir = useDirection();
  const inlineEndSide = dir === 'rtl' ? 'left' : 'right';
  // State, rather than a plain ref, makes the listener effect re-run when
  // NavItem swaps to a different SidebarMenuItem instance (for example when
  // its offline-gated branch flips).
  const [triggerNode, setTriggerNode] = useState<HTMLLIElement | null>(null);
  const setTriggerRef = useCallback((el: HTMLLIElement | null) => {
    setTriggerNode(el);
  }, []);
  const {
    isOpen,
    openViaHover,
    scheduleClose,
    keepOpen,
    handleOpenChange,
    handleContentOpenAutoFocus,
  } = useHomeHoverPreview({ baseChatHref });

  useEffect(() => {
    const el = triggerNode;
    if (!el) return;

    // A focus move within the row itself (e.g. onto a nested action) is not
    // a departure — only schedule the close once focus truly leaves the row,
    // mirroring the mouseleave/mouseenter transfer-grace pattern below.
    const handleFocusOut = (event: FocusEvent) => {
      if (el.contains(event.relatedTarget as Node | null)) return;
      scheduleClose();
    };

    el.addEventListener('mouseenter', openViaHover);
    el.addEventListener('mouseleave', scheduleClose);
    // Focus can preserve an already hover-open preview, but never creates
    // preview intent from closed.
    el.addEventListener('focusin', keepOpen);
    el.addEventListener('focusout', handleFocusOut);
    return () => {
      el.removeEventListener('mouseenter', openViaHover);
      el.removeEventListener('mouseleave', scheduleClose);
      el.removeEventListener('focusin', keepOpen);
      el.removeEventListener('focusout', handleFocusOut);
    };
  }, [triggerNode, openViaHover, scheduleClose, keepOpen]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {/*
       * `PopoverAnchor`, not `PopoverTrigger`: this popover is never opened
       * by a click — only by hover (and closed by Escape/outside
       * interaction/navigation via `handleOpenChange` and the router-events
       * effect above). `Trigger` composes its own click-to-toggle handler
       * onto this row, which — now that Home renders as a `Link` — would
       * race the row's own optimistic-nav click handling (both compose onto
       * the same `onClick` prop). `Anchor` only supplies the position
       * reference, so Home's click continues to run its own navigation
       * untouched, and the preview never flips open/closed from it either.
       */}
      <PopoverAnchor asChild>{children(setTriggerRef, isOpen)}</PopoverAnchor>
      <PopoverContent
        side={inlineEndSide}
        align="start"
        sideOffset={8}
        onMouseEnter={openViaHover}
        onMouseLeave={scheduleClose}
        // If focus enters the already hover-open content, cancel the trigger
        // row's pending focusout close so interaction inside stays stable.
        onFocus={keepOpen}
        onOpenAutoFocus={handleContentOpenAutoFocus}
        aria-label={intl.formatMessage(navV3Messages.homePreviewLabel)}
        style={{ width: SIDEBAR_WIDTH_NAV_V3 }}
        className="p-1"
        data-testid="home-hover-preview-content"
      >
        <HomePreviewContent />
      </PopoverContent>
    </Popover>
  );
};
